'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { BaseLineList } from '@/app/components/icons/BaseLineList';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { CreditsButton } from '@/app/components/shared/CreditsButton';
import { PCNotifications } from '@/app/components/shared/NotificationCenter';
import { MobileUserMenu, PCUserMenu } from '@/app/components/shared/UserCenter';
import { API_URL, isEnterpriseDistribution } from '@/app/constants';
import { useGlobalModal, useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { isPersonalSpaceId, reportEvent } from '@/app/utils';
import { EarnCreditsBtn } from './EarnCreditsBtn';
import { MobileMenu } from './MobileMenu';
import { TeamSpaceSwitcher } from './TeamSpaceSwitcher';

export const LoggedInHeaderSection: FC = () => {
  const { isMdScreen } = useIsScreen();
  const t = useTranslations();
  const [showMenu, setShowMenu] = useState(false);
  const [pCNotificationsPopoverOpen, setPCNotificationsPopoverOpen] = useState(false);
  const [pCUserMenuPopoverOpen, setPCUserMenuPopoverOpen] = useState(false);
  const { userInfo, teamSpaceInfo, isTeamSpace, isUserInfoInitialized } = useStore((state) => ({
    userInfo: state.userInfo,
    teamSpaceInfo: state.teamSpaceInfo,
    isTeamSpace: state.isTeamSpace,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));

  const { open: openTaskCenterDrawer } = useGlobalModal('taskCenterDrawer');

  const handlePopoverOpenChange = (open: boolean) => {
    setPCUserMenuPopoverOpen(open);
  };

  const handleOpenEarnCreditsDialog = () => {
    openTaskCenterDrawer({ fromPage: 'home_task_entry' });
  };

  useEffect(() => {
    if (!isMdScreen) {
      setShowMenu(false);
    }
  }, [isMdScreen]);

  // Temporarily hide the workbench earn entrance.
  // To restore, uncomment this block and reintroduce `useRef` in the React import.
  // const hasReportedEarnEntranceExposeRef = useRef(false);
  // const shouldShowWorkbenchEarnEntrance =
  //   !isMdScreen && isUserInfoInitialized && (!isTeamSpaceSelected || Boolean(teamSpaceInfo)) && !isTeamSpace;
  //
  // useEffect(() => {
  //   if (!shouldShowWorkbenchEarnEntrance || hasReportedEarnEntranceExposeRef.current) return;
  //
  //   reportEvent('earn_home_entrance_expose', { from_source: 'workbench' });
  //   hasReportedEarnEntranceExposeRef.current = true;
  // }, [shouldShowWorkbenchEarnEntrance]);

  const isTeamSpaceSelected = !isPersonalSpaceId(userInfo?.internal?.space_id);

  if (isMdScreen) {
    return (
      <>
        <div className="flex items-center space-x-3">
          <div className="flex size-9 items-center justify-center rounded-full bg-white/10">
            <BaseLineList className="rounded-8 text-white text-xl" onClick={() => setShowMenu((pre) => !pre)} />
          </div>
          <MobileUserMenu />
        </div>
        {showMenu && (
          <div className="fixed top-0 left-0 h-full w-full bg-[rgba(0,0,0,0.3)]">
            <MobileMenu onClose={() => setShowMenu(false)} />
          </div>
        )}
      </>
    );
  }

  // 用户信息初始化前，或团队空间信息尚未就绪时，先展示骨架，避免右侧内容闪动
  if (!isUserInfoInitialized || (isTeamSpaceSelected && !teamSpaceInfo)) {
    return (
      <div className="flex items-center gap-3">
        <div className="h-8 w-[140px] animate-pulse rounded-8 bg-system-white08" />
        <div className="size-9 animate-pulse rounded-full bg-system-white08" />
      </div>
    );
  }

  // 如果用户属于团队空间，则渲染团队空间切换组件
  if (isTeamSpace) {
    return (
      <div className="flex items-center gap-3">
        {!isEnterpriseDistribution && <TeamSpaceSwitcher />}
        <CreditsButton />
        <PCUserMenu popoverOpen={pCUserMenuPopoverOpen} onPopoverOpenChange={handlePopoverOpenChange} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <TeamSpaceSwitcher />
      {/* Temporarily hide the Vidu Earn header button.
          To restore, uncomment this block. */}
      {/*
      <ClientLink
        href="/earn"
        className="cursor-pointer rounded-8 border border-system-white08 bg-system-white04 px-3 py-1.5 text-sm text-system-white leading-[1.4]"
        onClick={() => {
          reportEvent('earn_home_entrance_click', { from_source: 'workbench' });
        }}
      >
        {t('header.tabs.viduEarn.label')}
      </ClientLink>
      */}
      <ClientLink
        href={API_URL}
        target="_blank"
        className="cursor-pointer rounded-8 border border-system-white08 bg-system-white04 px-3 py-1.5 text-sm text-system-white leading-[1.4]"
        onClick={() => {
          reportEvent('element_click', {
            element_name: 'API',
          });
        }}
      >
        {t('api-platform')}
      </ClientLink>
      <EarnCreditsBtn handleOpenEarnCreditsDialog={handleOpenEarnCreditsDialog} />
      <CreditsButton />
      <PCNotifications popoverOpen={pCNotificationsPopoverOpen} onPopoverOpenChange={setPCNotificationsPopoverOpen} />
      <PCUserMenu popoverOpen={pCUserMenuPopoverOpen} onPopoverOpenChange={handlePopoverOpenChange} />
    </div>
  );
};
